import { createSlice } from '@reduxjs/toolkit';

import type { ShareState } from '../types/compartilhamentoTypes';

import {
  acceptInvite,
  createSector,
  declineInvite,
  deleteSector,
  generateInviteLink,
  invitePerson,
  listSectorsSummary,
  loadInvitePreview,
  loadShareContext,
  makeItemPrivate,
  removePerson,
  searchShareSuggestions,
  toggleSectorAccess,
  updatePersonPermission,
  updateSector,
} from './actions';

const initialState: ShareState = {
  currentContext: null,
  people: [],
  sectorAccess: [],
  sectors: [],
  suggestions: [],
  inviteLink: null,
  isLoadingContext: false,
  isLoadingSectors: false,
  isSubmitting: false,
  isSearchingSuggestions: false,
  contextError: null,
  submitError: null,
  sectorsSummary: [],
  isLoadingSummary: false,
  summaryError: null,
  workspaceMembers: [],
  isLoadingMembers: false,
  invitePreview: null,

  isLoadingInvite: false,
  isSubmittingInvite: false,
  inviteError: null,
  inviteAccepted: false,
};

const compartilhamentoSlice = createSlice({
  name: 'compartilhamento',
  initialState,
  reducers: {
    clearShareContext: (state) => {
      state.currentContext = null;
      state.people = [];
      state.sectorAccess = [];
      state.sectors = [];
      state.suggestions = [];
      state.inviteLink = null;
      state.contextError = null;
      state.submitError = null;
    },
    clearShareSuggestions: (state) => {
      state.suggestions = [];
    },
    clearInviteState: (state) => {
      state.invitePreview = null;
      state.isLoadingInvite = false;
      state.isSubmittingInvite = false;
      state.inviteError = null;
      state.inviteAccepted = false;
    },
    clearSubmitError: (state) => {
      state.submitError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadShareContext.pending, (state) => {
        state.isLoadingContext = true;
        state.contextError = null;
      })
      .addCase(loadShareContext.fulfilled, (state, action) => {
        state.isLoadingContext = false;
        state.currentContext = action.payload.context;
        state.people = action.payload.people;
        state.sectorAccess = action.payload.sectorAccess;
        state.sectors = action.payload.sectors;
      })
      .addCase(loadShareContext.rejected, (state, action) => {
        state.isLoadingContext = false;
        state.contextError = (action.payload as string) ?? 'Erro ao carregar compartilhamento.';
      })

      .addCase(searchShareSuggestions.pending, (state) => {
        state.isSearchingSuggestions = true;
      })
      .addCase(searchShareSuggestions.fulfilled, (state, action) => {
        state.isSearchingSuggestions = false;
        state.suggestions = action.payload;
      })
      .addCase(searchShareSuggestions.rejected, (state) => {
        state.isSearchingSuggestions = false;
        state.suggestions = [];
      })

      .addCase(invitePerson.pending, (state) => {
        state.isSubmitting = true;
        state.submitError = null;
      })
      .addCase(invitePerson.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.people = action.payload.people;
      })
      .addCase(invitePerson.rejected, (state, action) => {
        state.isSubmitting = false;
        state.submitError = (action.payload as string) ?? 'Não foi possível enviar o convite.';
      })

      .addCase(updatePersonPermission.fulfilled, (state, action) => {
        state.people = action.payload;
      })

      .addCase(removePerson.fulfilled, (state, action) => {
        state.people = action.payload;
      })

      .addCase(toggleSectorAccess.fulfilled, (state, action) => {
        state.sectorAccess = action.payload;
      })

      .addCase(makeItemPrivate.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(makeItemPrivate.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.people = action.payload.people;
        state.sectorAccess = action.payload.sectorAccess;
      })
      .addCase(makeItemPrivate.rejected, (state, action) => {
        state.isSubmitting = false;
        state.submitError = (action.payload as string) ?? 'Não foi possível tornar privado.';
      })

      .addCase(generateInviteLink.fulfilled, (state, action) => {
        state.inviteLink = action.payload;
      })

      .addCase(listSectorsSummary.pending, (state) => {
        state.isLoadingSummary = true;
        state.summaryError = null;
      })
      .addCase(listSectorsSummary.fulfilled, (state, action) => {
        state.isLoadingSummary = false;
        state.sectorsSummary = action.payload;
      })
      .addCase(listSectorsSummary.rejected, (state, action) => {
        state.isLoadingSummary = false;
        state.summaryError = (action.payload as string) ?? 'Erro ao carregar setores.';
      })

      .addCase(createSector.fulfilled, (state, action) => {
        state.sectorsSummary = [action.payload, ...state.sectorsSummary];
      })

      .addCase(updateSector.fulfilled, (state, action) => {
        state.sectorsSummary = state.sectorsSummary.map((setor) =>
          setor.id === action.payload.id ? action.payload : setor,
        );
      })

      .addCase(deleteSector.fulfilled, (state, action) => {
        state.sectorsSummary = state.sectorsSummary.filter((s) => s.id !== action.payload);
      })

      .addCase(loadInvitePreview.pending, (state) => {
        state.isLoadingInvite = true;
        state.inviteError = null;
      })
      .addCase(loadInvitePreview.fulfilled, (state, action) => {
        state.isLoadingInvite = false;
        state.invitePreview = action.payload;
      })
      .addCase(loadInvitePreview.rejected, (state, action) => {
        state.isLoadingInvite = false;
        state.inviteError = (action.payload as string) ?? 'Erro ao carregar convite.';
      })

      .addCase(acceptInvite.pending, (state) => {
        state.isSubmittingInvite = true;
        state.inviteError = null;
      })
      .addCase(acceptInvite.fulfilled, (state, action) => {
        state.isSubmittingInvite = false;
        state.invitePreview = action.payload.invite;
        state.inviteAccepted = true;
      })
      .addCase(acceptInvite.rejected, (state, action) => {
        state.isSubmittingInvite = false;
        state.inviteError = (action.payload as string) ?? 'Não foi possível aceitar o convite.';
      })

      .addCase(declineInvite.pending, (state) => {
        state.isSubmittingInvite = true;
        state.inviteError = null;
      })
      .addCase(declineInvite.fulfilled, (state) => {
        state.isSubmittingInvite = false;
        state.invitePreview = null;
      })
      .addCase(declineInvite.rejected, (state, action) => {
        state.isSubmittingInvite = false;
        state.inviteError = (action.payload as string) ?? 'Não foi possível recusar o convite.';
      });
  },
});

export const { clearShareContext, clearShareSuggestions, clearInviteState, clearSubmitError } =
  compartilhamentoSlice.actions;

export default compartilhamentoSlice.reducer;
